import React, { useRef, useState } from 'react';
import { useDrag, useDrop, DropTargetMonitor, DragObjectWithType } from 'react-dnd';
import styled from 'styled-components';

import { MenuSectionType } from 'types/graphql';

import useHover from 'shared/hooks/use-hover';
import { Tooltip } from 'shared/components';
import { isScheduleActiveNow, hasScheduleBegun, Schedule } from 'shared/helpers/schedule';
import { useStores } from 'src/hooks/use-stores';
import {
  ImageContainer,
  Image,
} from 'src/dispensary-admin/customize/image-banner/image-banner-list/image-banner-list-item/image-banner-list-item.styles';
import { ImageBannerLinks } from 'src/dispensary-admin/customize/image-banner/image-banner-list/image-banner-list-item/image-banner-links';

import { ScheduleTooltipContent } from 'src/dispensary-admin/components/schedule-tooltip-content';
import { ScheduleCalendarIcon } from 'src/dispensary-admin/components/schedule-calendar-icon';
import { DragImage } from './drag-image';
import { RemoveButton } from './remove-button';
import { LocalSection } from '../../homepage-form.types';
import { PRODUCT_LIMIT, EDITABLE_SECTIONS } from '../../homepage-form.constants';

function getScheduleIconVariant(
  isScheduleActive: boolean,
  scheduleHasBegun: boolean,
  schedule: Schedule | null | undefined
): 'default' | 'inactive' | 'with-check' {
  if (!schedule) {
    return 'default';
  }
  if (isScheduleActive) {
    return 'with-check';
  }
  if (scheduleHasBegun) {
    return 'inactive';
  }
  return 'default';
}

type DragItem = DragObjectWithType & {
  id?: string;
  index?: number;
};

type DraggableOptionProps = {
  hasMobileApp: boolean;
  index: number;
  menuSection: LocalSection;
  onDrop: () => void;
  onMove: (dragIndex: number, hoverIndex: number) => void;
  onRemove: (menuSectionId: string) => void;
  onEdit: (id: string, type: MenuSectionType) => void;
  title: string;
};

const imgixParams = {
  crop: 'edges',
  fit: 'crop',
  h: 36,
  w: 62,
};

export function DraggableOption({
  hasMobileApp,
  index,
  menuSection,
  onDrop,
  onMove,
  onRemove,
  onEdit,
  title,
}: DraggableOptionProps): JSX.Element {
  const { count, id, sectionType, collectionSchedule } = menuSection;
  const { UI } = useStores();
  const [hoverRef, isHovered] = useHover();
  const ref = useRef<HTMLDivElement | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const isScheduleActive = collectionSchedule
    ? isScheduleActiveNow(collectionSchedule, UI.dispensary.timezone ?? 'UTC')
    : true;
  const scheduleHasBegun = collectionSchedule
    ? hasScheduleBegun(collectionSchedule, UI.dispensary.timezone ?? 'UTC')
    : false;

  const scheduleIconVariant = getScheduleIconVariant(isScheduleActive, scheduleHasBegun, collectionSchedule);

  const disabled = count <= 0 || !isScheduleActive;
  const isEditable = EDITABLE_SECTIONS.includes(sectionType);

  const handleWhenToMoveOptions = (item: DragItem, monitor: DropTargetMonitor): void => {
    if (!ref.current) {
      return;
    }

    const dragIndex = item.index ?? 0;
    const hoverIndex = index;

    if (dragIndex === hoverIndex) {
      return;
    }

    const hoverBoundingRect = ref.current.getBoundingClientRect();
    const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
    const clientOffset = monitor.getClientOffset();

    if (!clientOffset) {
      return;
    }

    const hoverClientY = clientOffset.y - hoverBoundingRect.top;

    if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
      return;
    }

    if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
      return;
    }

    onMove(dragIndex, hoverIndex);
    item.index = hoverIndex;
  };

  const [, drop] = useDrop({
    accept: 'option',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    drop: (_item, _monitor) => onDrop(),
    hover: (item, monitor) => handleWhenToMoveOptions(item, monitor),
  });
  const [{ isDragging }, drag] = useDrag({
    item: { type: 'option', id, index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });
  drag(drop(ref));

  const handleClickRemove = (): void => {
    onRemove(id);
  };

  const handleEditClick = (): void => {
    onEdit(id, sectionType);
  };

  return (
    <DraggableOptionStyles disabled={disabled} isDragging={isDragging} ref={ref}>
      <HoverContainer ref={hoverRef}>
        <Text>
          {sectionType === MenuSectionType.image ? (
            <ImageContainer>
              <Image
                imgixParams={imgixParams}
                src={menuSection.image?.image ?? ''}
                disableSrcSet
                htmlAttributes={{ alt: menuSection.image?.alt ?? '' }}
              />
            </ImageContainer>
          ) : (
            <>
              <Title disabled={disabled}>
                <b>{title}</b>
              </Title>
              <Separator>|</Separator>
              <Subtitle disabled={disabled}>
                {count} {sectionType === `OFFERS` ? `offers` : `products`}
                {count === PRODUCT_LIMIT && ` (max)`}
              </Subtitle>
            </>
          )}

          {collectionSchedule && (
            <>
              <Separator>|</Separator>
              <TooltipWrapper onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
                <ScheduleCalendarIcon variant={scheduleIconVariant} disabled={disabled} />
                <Subtitle disabled={disabled}>Scheduled</Subtitle>
                <Tooltip
                  hideIcon
                  tooltipCopy={<ScheduleTooltipContent schedule={collectionSchedule} />}
                  visible={showTooltip}
                  left='auto'
                  right='7px'
                  width='180px'
                  position='absolute'
                />
              </TooltipWrapper>
            </>
          )}

          {sectionType === MenuSectionType.image && menuSection.image ? (
            <div>
              {isEditable && (
                <EditButton onClick={handleEditClick} margin='0 0 4px 0'>
                  Edit
                </EditButton>
              )}
              <ImageBannerLinks banner={menuSection.image} hasMobileApp={hasMobileApp} showLinkText={false} />
            </div>
          ) : (
            isEditable && <EditButton onClick={handleEditClick}>Edit</EditButton>
          )}
        </Text>
        <ButtonContainer>
          <RemoveButton
            data-cy={`remove-${sectionType}`}
            data-test={`remove-${sectionType}`}
            onClick={handleClickRemove}
            visible={isHovered}
          />
        </ButtonContainer>
      </HoverContainer>
      <DragImage disabled={disabled} />
    </DraggableOptionStyles>
  );
}

const DraggableOptionStyles = styled.div<{ disabled: boolean; isDragging: boolean }>`
  align-items: center;
  background: ${({ disabled }) => (disabled ? '#F3F6F8' : '#FFFFFF')};
  border-radius: 6px;
  border: 1px solid #d3d8de;
  box-shadow: ${({ disabled }) => (disabled ? 'none' : '0px 3px 5px #E9ECF1')};
  display: flex;
  height: 72px;
  justify-content: space-between;
  margin-bottom: 19px;
  opacity: ${({ isDragging }) => (isDragging ? 0 : 1)};
  padding-right: 21px;
  width: 100%;
`;

const HoverContainer = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: space-between;
  padding-left: 25px;
  width: 100%;
`;

const Text = styled.div`
  align-items: center;
  display: flex;
  flex: 1 0 0%;
  min-width: 0;
`;

const Title = styled.div<{ disabled: boolean }>`
  color: ${({ disabled }) => (disabled ? '#969EA5' : '#677882')};
  font-size: 13px;
  line-height: 15px;

  b {
    font-weight: bold;
    color: ${({ disabled }) => (disabled ? '#969EA5' : '#4F5D68')};
  }
`;

const Separator = styled.div`
  margin: 0 12px;
`;

const Subtitle = styled.div<{ disabled: boolean }>`
  color: ${({ disabled }) => (disabled ? '#969EA5' : '#677882')};
`;

const ButtonContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
`;

const EditButton = styled.button<{ margin?: string }>`
  appearance: none;
  border: none;
  padding: 0;
  margin: ${({ margin }) => margin ?? '0 10px'};
  font-size: 13px;
  line-height: 15px;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.primaryBlue};
  cursor: pointer;
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
`;
